// Dashboard.jsx
import { useState, useEffect } from 'react';
import { get } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const Dashboard = () => {
  const [stats, setStats] = useState({
    teamCount: 0,
    carCount: 0,
    eventCount: 0,
    galleryCount: 0,
    productCount: 0,
    sponsorCount: 0,
    achievementCount: 0,
    messageCount: 0,
    unreadMessageCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const language = getUserLanguage();

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [
          teamData,
          carData,
          eventData,
          galleryData,
          productData,
          sponsorData,
          achievementData,
          messageData,
        ] = await Promise.all([
          get('/team-members'),
          get('/cars'),
          get('/events'),
          get('/gallery'),
          get('/products'),
          get('/sponsors'),
          get('/achievements'),
          get('/contact-messages'),
        ]);

        setStats({
          teamCount: teamData?.length || 0,
          carCount: carData?.length || 0,
          eventCount: eventData?.length || 0,
          galleryCount: galleryData?.length || 0,
          productCount: productData?.length || 0,
          sponsorCount: sponsorData?.length || 0,
          achievementCount: achievementData?.length || 0,
          messageCount: messageData?.length || 0,
          unreadMessageCount: messageData?.filter(msg => !msg.is_read)?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="admin-p-4 admin-flex admin-justify-center">
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  const statItems = [
    { title: language === 'cs' ? 'Členů týmu' : 'Team Members', value: stats.teamCount, icon: 'fas fa-users', color: 'admin-bg-blue-500' },
    { title: language === 'cs' ? 'Vozů' : 'Cars', value: stats.carCount, icon: 'fas fa-car', color: 'admin-bg-green-500' },
    { title: language === 'cs' ? 'Událostí' : 'Events', value: stats.eventCount, icon: 'fas fa-calendar-alt', color: 'admin-bg-yellow-500' },
    { title: language === 'cs' ? 'Položek v galerii' : 'Gallery Items', value: stats.galleryCount, icon: 'fas fa-images', color: 'admin-bg-purple-500' },
    { title: language === 'cs' ? 'Produktů' : 'Products', value: stats.productCount, icon: 'fas fa-shopping-cart', color: 'admin-bg-pink-500' },
    { title: language === 'cs' ? 'Sponzorů' : 'Sponsors', value: stats.sponsorCount, icon: 'fas fa-handshake', color: 'admin-bg-indigo-500' },
    { title: language === 'cs' ? 'Úspěchů' : 'Achievements', value: stats.achievementCount, icon: 'fas fa-trophy', color: 'admin-bg-red-500' },
    { title: language === 'cs' ? 'Zpráv' : 'Messages', value: stats.messageCount, icon: 'fas fa-envelope', color: 'admin-bg-teal-500' },
    { title: language === 'cs' ? 'Nepřečtených zpráv' : 'Unread Messages', value: stats.unreadMessageCount, icon: 'fas fa-envelope-open', color: 'admin-bg-orange-500' },
  ];

  return (
    <div className="admin-p-4">
      <h1 className="admin-text-2xl admin-font-bold admin-mb-6">
        {language === 'cs' ? 'Přehled' : 'Dashboard'}
      </h1>

      <section>
        <h2 className="admin-text-xl admin-font-semibold admin-mb-4 admin-text-white">
          {language === 'cs' ? 'Přehled webu' : 'Website Overview'}
        </h2>
        <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-3 admin-gap-6">
          {statItems.map((item, index) => (
            <div key={index} className="admin-card admin-bg-dark-gray admin-p-6 admin-flex admin-items-center">
              <div className={`${item.color} admin-p-4 admin-rounded-lg admin-text-white admin-mr-4`}>
                <i className={`${item.icon} admin-text-xl`}></i>
              </div>
              <div>
                <h3 className="admin-text-lg admin-font-semibold admin-text-white">{item.value}</h3>
                <p className="admin-text-gray-300">{item.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-mt-8">
        <h2 className="admin-text-xl admin-font-semibold admin-mb-4 admin-text-white">
          {language === 'cs' ? 'Rychlé odkazy' : 'Quick Links'}
        </h2>
        <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
          <div className="admin-card admin-bg-dark-gray admin-p-6">
            <h3 className="admin-text-lg admin-font-semibold admin-mb-4 admin-text-white">
              {language === 'cs' ? 'Správa obsahu' : 'Content Management'}
            </h3>
            <div className="admin-grid admin-grid-cols-2 admin-gap-4">
              <a href="/admin/team" className="admin-btn admin-btn-primary">
                <i className="fas fa-users admin-mr-2"></i> {language === 'cs' ? 'Tým' : 'Team'}
              </a>
              <a href="/admin/cars" className="admin-btn admin-btn-primary">
                <i className="fas fa-car admin-mr-2"></i> {language === 'cs' ? 'Auta' : 'Cars'}
              </a>
              <a href="/admin/events" className="admin-btn admin-btn-primary">
                <i className="fas fa-calendar-alt admin-mr-2"></i> {language === 'cs' ? 'Události' : 'Events'}
              </a>
              <a href="/admin/gallery" className="admin-btn admin-btn-primary">
                <i className="fas fa-images admin-mr-2"></i> {language === 'cs' ? 'Galerie' : 'Gallery'}
              </a>
            </div>
          </div>

          <div className="admin-card admin-bg-dark-gray admin-p-6">
            <h3 className="admin-text-lg admin-font-semibold admin-mb-4 admin-text-white">
              {language === 'cs' ? 'Zprávy a nastavení' : 'Messages & Settings'}
            </h3>
            <div className="admin-grid admin-grid-cols-2 admin-gap-4">
              <a href="/admin/messages" className="admin-btn admin-btn-primary admin-flex admin-items-center admin-justify-center">
                <i className="fas fa-envelope admin-mr-2"></i> {language === 'cs' ? 'Zprávy' : 'Messages'}
                {stats.unreadMessageCount > 0 && (
                  <span className="admin-ml-2 admin-px-2 admin-py-1 admin-text-xs admin-bg-red-600 admin-text-white admin-rounded-full">
                    {stats.unreadMessageCount}
                  </span>
                )}
              </a>
              <a href="/admin/settings" className="admin-btn admin-btn-primary">
                <i className="fas fa-cog admin-mr-2"></i> {language === 'cs' ? 'Nastavení' : 'Settings'}
              </a>
              <a href="/admin/achievements" className="admin-btn admin-btn-primary">
                <i className="fas fa-trophy admin-mr-2"></i> {language === 'cs' ? 'Úspěchy' : 'Achievements'}
              </a>
              <a href="/admin/sponsors" className="admin-btn admin-btn-primary">
                <i className="fas fa-handshake admin-mr-2"></i> {language === 'cs' ? 'Sponzoři' : 'Sponsors'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-mt-8">
        <h2 className="admin-text-xl admin-font-semibold admin-mb-4 admin-text-white">
          {language === 'cs' ? 'Nápověda a podpora' : 'Help & Support'}
        </h2>
        <div className="admin-card admin-bg-dark-gray admin-p-6">
          <p className="admin-mb-4 admin-text-gray-300">
            {language === 'cs'
              ? 'Potřebujete pomoc s administrací? Kontaktujte podporu.'
              : 'Need help with the administration? Contact support.'}
          </p>
          <a href="mailto:support@norisknodrift.com" className="admin-btn admin-btn-primary">
            <i className="fas fa-envelope admin-mr-2"></i>
            {language === 'cs' ? 'Kontaktovat podporu' : 'Contact Support'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;